/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { GhostIcon } from "lucide-react"

import { cn } from "@/utils/cn"

import { AppRoutes } from "../../routes/routes"
import { buttonVariants } from "../primitives/button"
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../primitives/empty"

/**
 * The page for a path that matches no route. There are two ways in.
 *
 * A full reload to `/404`. `asyncInit` calls `forceRedirect404()` for an unknown
 * path, and `loadDashboard` does the same when it finds no user. `isValidRoute`
 * rejects `/404` itself, so `asyncInit` returns before it loads the session. The
 * page has no sidebar, signed in or not.
 *
 * The catch-all route, after a client-side navigation to an unknown path. The URL
 * stays and the session from the last full load is still there, so the sidebar
 * shows. The layout has to work beside it too.
 */
export const PageNotFoundView = () => (
    // TODO: Remove `border-0` when preflight loads at cutover (#1166). Until then the stock
    // `border-dashed` gets the browser's 3px default border width and draws a frame.
    <Empty className="min-h-dvh border-0">
        <EmptyHeader>
            <EmptyMedia>
                <GhostIcon className="size-10 text-muted-foreground" />
            </EmptyMedia>
            <EmptyTitle>
                <h1 className="m-0 text-2xl font-bold">Page not found</h1>
            </EmptyTitle>
            <EmptyDescription className="text-foreground/70">
                Sorry, the page you&apos;re looking for doesn&apos;t exist.
            </EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
            {/*
             * A plain `<a>` on purpose. `asyncInit` skipped the session on `/404`,
             * and only a full page load runs it again.
             */}
            <a href={AppRoutes.Home} className={cn(buttonVariants({ size: "lg" }), "font-bold uppercase no-underline")}>
                Go to Dashboard
            </a>
        </EmptyContent>
    </Empty>
)
